/**
 * Audit Log Table Component
 * Displays audit logs in a table format with infinite scroll
 */

import React, { useRef, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Clock, Zap } from 'lucide-react';
import { AuditLog } from '@/types/audit';
import { formatDistanceToNow } from 'date-fns';

interface AuditLogTableProps {
  logs: AuditLog[];
  isLoading: boolean;
  onLoadMore: () => void;
  hasMore: boolean;
}

export const AuditLogTable: React.FC<AuditLogTableProps> = ({
  logs,
  isLoading,
  onLoadMore,
  hasMore,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(new Set());

  // Handle infinite scroll
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      if (scrollHeight - scrollTop <= clientHeight * 1.5 && hasMore && !isLoading) {
        onLoadMore();
      }
    };

    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [onLoadMore, hasMore, isLoading]);

  const toggleRowExpansion = (id: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getStatusBadge = (status: AuditLog['status']) => {
    const variants = {
      success: 'default',
      error: 'destructive',
      pending: 'secondary',
      warning: 'outline',
    } as const;

    const colors = {
      success: 'text-green-600 border-green-600',
      error: 'text-red-600 border-red-600',
      pending: 'text-yellow-600 border-yellow-600',
      warning: 'text-orange-600 border-orange-600',
    };

    return (
      <Badge variant={variants[status]} className={colors[status]}>
        {status}
      </Badge>
    );
  };

  const getActionIcon = (action: AuditLog['action']) => {
    const icons = {
      'Plan Created': '📋',
      'Step Completed': '✅',
      'Tool Used': '🔧',
      'User Action': '👤',
      'System Event': '⚙️',
    };
    return icons[action] || '📄';
  };

  if (logs.length === 0 && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">No audit logs found</p>
        <p className="text-sm text-muted-foreground mt-2">
          Logs will appear here as actions are performed
        </p>
      </div>
    );
  }

  return (
    <ScrollArea 
      ref={scrollRef}
      className="h-[600px] rounded-md border"
      data-testid="audit-scroll-area"
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]"></TableHead>
            <TableHead className="w-[180px]">Timestamp</TableHead>
            <TableHead className="w-[150px]">Action</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="w-[100px]">Status</TableHead>
            <TableHead className="w-[100px]">Duration</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => {
            const isExpanded = expandedRows.has(log.id);
            return (
              <React.Fragment key={log.id}>
                <TableRow className="cursor-pointer hover:bg-muted/50">
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleRowExpansion(log.id)}
                      className="p-1 h-auto"
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{getActionIcon(log.action)}</span>
                      <span className="text-sm">{log.action}</span>
                    </div>
                  </TableCell>
                  <TableCell>{log.description}</TableCell>
                  <TableCell>{getStatusBadge(log.status)}</TableCell>
                  <TableCell>
                    {log.duration && (
                      <div className="flex items-center gap-1">
                        <Zap className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">{log.duration}ms</span>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
                {isExpanded && log.tool && (
                  <TableRow>
                    <TableCell colSpan={6} className="bg-muted/30">
                      <Collapsible open={isExpanded}>
                        <CollapsibleContent>
                          <div className="p-4 space-y-4">
                            <div>
                              <h4 className="text-sm font-semibold mb-2">Tool Details</h4>
                              <div className="space-y-2">
                                <div>
                                  <span className="text-sm text-muted-foreground">Tool Name: </span>
                                  <code className="text-sm bg-muted px-2 py-1 rounded">
                                    {log.tool.name}
                                  </code>
                                </div>
                                <div>
                                  <span className="text-sm text-muted-foreground">Payload: </span>
                                  <pre className="text-sm bg-muted p-2 rounded overflow-x-auto">
                                    {JSON.stringify(log.tool.payload, null, 2)}
                                  </pre>
                                </div>
                                <div>
                                  <span className="text-sm text-muted-foreground">Result: </span>
                                  <pre className="text-sm bg-muted p-2 rounded overflow-x-auto">
                                    {JSON.stringify(log.tool.result, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                            {log.metadata && (
                              <div>
                                <h4 className="text-sm font-semibold mb-2">Metadata</h4>
                                <pre className="text-sm bg-muted p-2 rounded overflow-x-auto">
                                  {JSON.stringify(log.metadata, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
          {isLoading && (
            <>
              {[...Array(5)].map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell colSpan={6}>
                    <Skeleton className="h-12 w-full" data-testid="loading-skeleton" />
                  </TableCell>
                </TableRow>
              ))}
            </>
          )}
        </TableBody>
      </Table>
    </ScrollArea>
  );
};